import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, GraduationCap, Users, CheckCircle, Building, ArrowRight, Award, Clock, Phone } from 'lucide-react';
import SEO from '../components/SEO';

const EducationalInstitutes = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "CIRO Exam Study Guides - Educational Institution Package",
    "description": "Bulk order CIRE and RSE exam study guides for educational institutions, colleges, and training programs in Canada.",
    "brand": {
      "@type": "Brand",
      "name": "Vitta Finance Academy"
    },
    "offers": {
      "@type": "Offer",
      "price": "269.99",
      "priceCurrency": "CAD",
      "availability": "https://schema.org/InStock"
    }
  };

  const benefits = [
    {
      icon: <BookOpen size={32} color="#1e40af" />,
      title: 'Comprehensive Study Materials',
      description: 'Complete CIRE and RSE exam preparation guides covering all CIRO syllabus elements'
    },
    {
      icon: <Users size={32} color="#1e40af" />,
      title: 'Bulk Ordering',
      description: 'Streamlined ordering process for multiple students with institutional pricing'
    },
    {
      icon: <Award size={32} color="#1e40af" />,
      title: 'Proven Track Record',
      description: 'Trusted by over 30,000 learners across CSC, FRM, CFA and IFC courses'
    },
    {
      icon: <Clock size={32} color="#1e40af" />,
      title: 'Fast Delivery',
      description: 'Quick turnaround for bulk orders to meet your program schedules'
    }
  ];

  const includedBooks = [
    {
      title: 'CIRE Exam Study Guide',
      description: 'Canadian Investment Regulatory Exam - Complete 9-element coverage',
      elements: '9 CIRO Elements',
      pages: '700+ Pages'
    },
    {
      title: 'RSE Exam Study Guide',
      description: 'Retail Securities Exam - Comprehensive KYC, Fixed Income, Equities coverage',
      elements: '9 RSE Elements',
      pages: '700+ Pages'
    }
  ];

  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title="Educational Institutes - Bulk Order CIRE & RSE Study Guides | Vitta Finance Academy"
        description="Order CIRE and RSE exam study guides for your educational institution, college, or training program. Bulk pricing at $269.99 CAD. Trusted by 30,000+ learners."
        keywords="CIRE study guide bulk order, RSE study guide educational institution, CIRO exam books colleges, securities exam training materials, bulk order study guides Canada"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(255, 255, 255, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '20px',
            padding: '8px 20px',
            marginBottom: '24px'
          }}>
            <GraduationCap size={18} />
            <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>FOR EDUCATIONAL INSTITUTIONS</span>
          </div>
          
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '900',
            marginBottom: '20px',
            lineHeight: 1.2
          }}>
            CIRO Exam Study Materials for Schools & Colleges
          </h1>
          
          <p style={{
            fontSize: '1.25rem',
            opacity: 0.95,
            maxWidth: '700px',
            margin: '0 auto 32px',
            lineHeight: 1.6
          }}>
            Equip your students with comprehensive CIRE or RSE exam preparation materials. 
            Bulk ordering available for educational institutions across Canada.
          </p>

          <a 
            href="https://vittafinance.mykajabi.com/offers/6DWf4fGF/checkout"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="edu-order-hero-btn"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              padding: '18px 36px',
              background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
              color: 'white',
              borderRadius: '12px',
              textDecoration: 'none',
              fontSize: '1.1rem',
              fontWeight: '700',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              transition: 'transform 0.2s'
            }}
          >
            <BookOpen size={22} />
            Order Now - $269.99 CAD per book
            <ArrowRight size={20} />
          </a>
        </div>
      </section>

      {/* Package Details */}
      <section style={{ padding: '80px 20px', background: 'white' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '50px' }}>
            <h2 style={{
              fontFamily: 'Georgia, serif',
              fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
              fontWeight: '700',
              color: '#0f172a',
              marginBottom: '16px'
            }}>
              Study Guides for Educational Institutions
            </h2>
            <p style={{
              fontSize: '1.1rem',
              color: '#64748b',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Order CIRE or RSE study guides individually for your students
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '30px',
            marginBottom: '50px'
          }}>
            {/* CIRE Book */}
            <div style={{
              background: 'white',
              border: '2px solid #1e40af',
              borderRadius: '16px',
              padding: '32px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <BookOpen size={30} color="white" />
              </div>
              
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#1e40af',
                marginBottom: '8px'
              }}>CIRE Exam Study Guide</h3>
              
              <p style={{
                fontSize: '0.95rem',
                color: '#475569',
                marginBottom: '20px',
                lineHeight: 1.6
              }}>Canadian Investment Regulatory Exam - Complete 9-element coverage</p>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginBottom: '24px'
              }}>
                <span style={{
                  background: '#dbeafe',
                  color: '#1e40af',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}>9 CIRO Elements</span>
                <span style={{
                  background: '#dcfce7',
                  color: '#166534',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}>700+ Pages</span>
              </div>

              <div style={{
                fontSize: '2.5rem',
                fontWeight: '900',
                color: '#1e40af',
                marginBottom: '8px'
              }}>
                $269.99 <span style={{ fontSize: '1rem', fontWeight: '500', color: '#64748b' }}>CAD</span>
              </div>
              <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '20px' }}>per book</p>

              <a 
                href="https://vittafinance.mykajabi.com/offers/6DWf4fGF/checkout"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="edu-order-cire-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                  color: 'white',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontWeight: '700'
                }}
              >
                Order CIRE Guide
                <ArrowRight size={18} />
              </a>
            </div>

            {/* RSE Book */}
            <div style={{
              background: 'white',
              border: '2px solid #166534',
              borderRadius: '16px',
              padding: '32px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #166534 0%, #22c55e 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <BookOpen size={30} color="white" />
              </div>
              
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#166534',
                marginBottom: '8px'
              }}>RSE Exam Study Guide</h3>
              
              <p style={{
                fontSize: '0.95rem',
                color: '#475569',
                marginBottom: '20px',
                lineHeight: 1.6
              }}>Retail Securities Exam - Comprehensive KYC, Fixed Income, Equities coverage</p>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginBottom: '24px'
              }}>
                <span style={{
                  background: '#dcfce7',
                  color: '#166534',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}>9 RSE Elements</span>
                <span style={{
                  background: '#dcfce7',
                  color: '#166534',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}>700+ Pages</span>
              </div>

              <div style={{
                fontSize: '2.5rem',
                fontWeight: '900',
                color: '#166534',
                marginBottom: '8px'
              }}>
                $269.99 <span style={{ fontSize: '1rem', fontWeight: '500', color: '#64748b' }}>CAD</span>
              </div>
              <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '20px' }}>per book</p>

              <a 
                href="https://vittafinance.mykajabi.com/offers/6DWf4fGF/checkout"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="edu-order-rse-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #166534 0%, #22c55e 100%)',
                  color: 'white',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontWeight: '700'
                }}
              >
                Order RSE Guide
                <ArrowRight size={18} />
              </a>
            </div>
          </div>

          {/* What's Included */}
          <div style={{
            background: '#f8fafc',
            borderRadius: '16px',
            padding: '40px',
            textAlign: 'center'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#0f172a',
              marginBottom: '24px'
            }}>Each Study Guide Includes</h3>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '16px'
            }}>
              {[
                'Complete Syllabus Coverage',
                'All 9 CIRO Elements',
                '700+ Pages of Content',
                'Exam Tips & Strategies',
                'Written by Industry Experts'
              ].map((feature, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'white',
                  padding: '10px 18px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <CheckCircle size={18} color="#22c55e" />
                  <span style={{ fontSize: '0.95rem', color: '#334155', fontWeight: '500' }}>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section style={{ padding: '80px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '50px' }}>
            <h2 style={{
              fontFamily: 'Georgia, serif',
              fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
              fontWeight: '700',
              color: '#0f172a',
              marginBottom: '16px'
            }}>
              Why Choose Vitta Finance Academy?
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '30px'
          }}>
            {benefits.map((benefit, idx) => (
              <div key={idx} style={{
                background: 'white',
                borderRadius: '16px',
                padding: '32px',
                border: '1px solid #e2e8f0',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '70px',
                  height: '70px',
                  background: '#dbeafe',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  {benefit.icon}
                </div>
                <h3 style={{
                  fontSize: '1.15rem',
                  fontWeight: '700',
                  color: '#0f172a',
                  marginBottom: '12px'
                }}>{benefit.title}</h3>
                <p style={{
                  fontSize: '0.95rem',
                  color: '#64748b',
                  lineHeight: 1.6
                }}>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal For Section */}
      <section style={{ padding: '80px 20px', background: 'white' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 style={{
              fontFamily: 'Georgia, serif',
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              fontWeight: '700',
              color: '#0f172a',
              marginBottom: '16px'
            }}>
              Ideal For
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px'
          }}>
            {[
              { icon: <GraduationCap size={24} />, label: 'Colleges & Universities' },
              { icon: <Building size={24} />, label: 'Financial Training Institutes' },
              { icon: <Users size={24} />, label: 'Corporate Training Programs' },
              { icon: <Award size={24} />, label: 'Professional Development Centers' }
            ].map((item, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                padding: '20px 24px',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ color: '#1e40af' }}>{item.icon}</div>
                <span style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#0f172a'
                }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
            fontWeight: '700',
            marginBottom: '16px'
          }}>
            Need a Custom Quote?
          </h2>
          <p style={{
            fontSize: '1.1rem',
            opacity: 0.95,
            marginBottom: '32px',
            lineHeight: 1.6
          }}>
            For large orders or custom institutional packages, contact us directly for special pricing.
          </p>
          
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <a 
              href="https://vittafinance.mykajabi.com/offers/6DWf4fGF/checkout"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="edu-order-cta-btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '16px 32px',
                background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                color: 'white',
                borderRadius: '10px',
                textDecoration: 'none',
                fontSize: '1rem',
                fontWeight: '700'
              }}
            >
              <BookOpen size={20} />
              Order Now - $269.99 CAD per book
            </a>
            <Link 
              to="/contact"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '16px 32px',
                background: 'white',
                color: '#1e40af',
                borderRadius: '10px',
                textDecoration: 'none',
                fontSize: '1rem',
                fontWeight: '700'
              }}
            >
              <Phone size={20} />
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EducationalInstitutes;
